import type {GroupedCartEntry, ResolvedGroupedCartEntry, ResolvedGroupedCartEntryKIT} from "~/Cart/models/CartData";
import type {RootState} from "~/redux/store";
import {selectCartEntry, selectLineItem} from "~/Cart/redux/selectors/cartSelectors";
import {selectProductDescriptor} from "~/redux/products/productsSelectors";
import {shallowEqual} from "react-redux";
import uniq from "lodash/uniq";
import {getPackageCode} from "~/utils/cartHelper";
import {getProductData, type ProductData} from "~/models/ProductData";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

const getAllLineItems = (entry: ResolvedGroupedCartEntry) => entry.packGroups.flatMap((group) => group.packEntries);

export const selectResolvedGroupedEntry =
  (entry: GroupedCartEntry) =>
  (state: RootState): ResolvedGroupedCartEntry => {
    const resolved: ResolvedGroupedCartEntry = {
      ...entry,
      cartEntry: selectCartEntry(entry.cartEntryId)(state),
      descriptors: [],
      packGroups: entry.packGroups.map((packGroup) => ({
        ...packGroup,
        packEntries: packGroup.packEntryIds.map((id) => selectLineItem(id)(state)),
      })),
    };

    const productCode = resolved.cartEntry.productCode;

    const priceReferences = uniq(getAllLineItems(resolved).map((lineItem) => lineItem.priceReference));

    resolved.descriptors = priceReferences.map((priceReference) =>
      selectProductDescriptor(state, {
        productCode,
        priceReference,
      }),
    );

    return resolved;
  };
export const compareRGCEEquality = (left: ResolvedGroupedCartEntry, right: ResolvedGroupedCartEntry): boolean => {
  if (!shallowEqual(left.cartEntry, right.cartEntry)) {
    return false;
  }

  if (!shallowEqual(left.descriptors, right.descriptors)) {
    return false;
  }

  return left.packGroups.every((leftPackGroup, index) => {
    return shallowEqual(leftPackGroup.packEntries, right.packGroups[index].packEntries);
  });
};

export function isProductEntryFcc(entry: ResolvedGroupedCartEntry | ResolvedGroupedCartEntryKIT): boolean {
  let productData;
  let lineItems;
  if ("descriptors" in entry) {
    productData = entry.descriptors[0].base;
    lineItems = entry.packGroups.flatMap((group) => group.packEntries);
  } else {
    productData = entry.productDescriptor.base;
    lineItems = [entry.packEntry];
  }

  const fccPackageCodes = getFccPackCodes(productData);
  const isProductFcc = lineItems.some((entry) => fccPackageCodes.includes(entry.packCode));
  return isProductFcc;
}

function getFccPackCodes(product: ProductData | ProductDescriptor) {
  const productData = getProductData(product);
  return Object.values(productData.fifteenDigit)
    .filter((val) => val?.isFcc)
    .map((val) => getPackageCode(val.materialNo));
}
